import { format0 } from 'utils/number';
import type { DatasetFacets, FacetBucket } from '../types';

/**
 * Pulls a trailing "(XX)" code out of a facet bucket name like "Texas (TX)" for
 * a compact tag/heading; falls back to the full name when there's no code.
 * @param {string} name The bucket name.
 * @return {string} The code, or the name.
 */
export const shortLabel = (name: string): string => {
  const match = /\(([^)]+)\)\s*$/.exec(name);
  return match ? match[1] : name;
};

/**
 * Facet bucket names are raw ES values (the STI class demodulized), so the AFE
 * document type reads "Afe". Presents it upper-cased wherever a bucket name
 * surfaces (chips, checkboxes, color-by options).
 * @param {string} name The bucket name.
 * @return {string} The name to show.
 */
export const displayFacetName = (name: string): string => {
  return name === 'Afe' ? 'AFE' : name;
};

/**
 * Documents value lists read alphabetically with the AFE type in all caps;
 * other datasets keep the facet's native bucket order.
 * @param {Array} items The facet items.
 * @param {boolean} isDocuments Sort alphabetically, AFE in caps.
 * @param {object} [options] How to sort.
 * @param {boolean} [options.numeric] Order the numbers in names by value, so
 *   "2 Main St" comes before "12 Main St".
 * @return {Array} The items in display order.
 */
export const orderFacetItems = <T extends { name: string }>(
  items: T[],
  isDocuments: boolean,
  { numeric = false }: { numeric?: boolean } = {}
): T[] => {
  if (!isDocuments) return items;
  return items
    .map((item) => ({ ...item, name: displayFacetName(item.name) }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric }));
};

/**
 * Resolves a facet-driven value (stored as an id) to its bucket name so a chip
 * reads "TX" / "Courthouse", not "48" / "courthouse". Falls back to the raw
 * value when the field has no facet or the bucket isn't loaded.
 * @param {DatasetFacets | null | undefined} facets The loaded facets.
 * @param {string | undefined} facetKey The field's facet.
 * @param {string} val The stored value.
 * @return {string} The label.
 */
export const labelForFacetValue = (
  facets: DatasetFacets | null | undefined,
  facetKey: string | undefined,
  val: string
): string => {
  if (!facetKey || !facets) return displayFacetName(val);
  const bucket = facets[facetKey]?.buckets?.find(
    (b) => String(b.id) === String(val)
  );
  return displayFacetName(bucket?.name ?? val);
};

/**
 * A filter option's label: its name and document count, or its name alone for
 * an option-only facet, which carries no count, or where counts are left out.
 * @param {FacetBucket} bucket The option.
 * @param {string} [countNoun] Follows the count, e.g. "Documents".
 * @param {object} [options] What the label shows.
 * @param {boolean} [options.showCount] The count beside the name.
 * @return {string} The label.
 */
export function bucketLabel(
  bucket: FacetBucket,
  countNoun?: string,
  { showCount = true }: { showCount?: boolean } = {}
): string {
  if (!showCount || bucket.count === undefined) return bucket.name;
  return `${bucket.name} (${format0(bucket.count)}${countNoun ? ` ${countNoun}` : ''})`;
}
